import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Title,
  Text,
  Group,
  TextInput,
  PasswordInput,
  Button,
  Grid,
  Card,
  Badge,
  Code,
  Accordion,
  Alert,
  Tabs,
  ScrollArea,
  Loader,
  CopyButton,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconServer,
  IconKey,
  IconLock,
  IconTerminal,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconInfoCircle,
  IconShieldCheck,
  IconWorld,
  IconDatabase
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

export default function MCPServerTab({ user, API_BASE_URL }) {
  const { t } = useTranslation();
  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [pin, setPin] = useState('');
  const [selectedTool, setSelectedTool] = useState('website_get_preview');
  const [testArgs, setTestArgs] = useState('{\n  "email": "' + (user ? user.email : '') + '"\n}');
  const [testResult, setTestResult] = useState(null);
  const [executing, setExecuting] = useState(false);

  const endpointUrl = 'https://www.ailinkb.com/api/mcp';
  const toolsUrl = `${API_BASE_URL || 'https://www.ailinkb.com'}/api/mcp/tools`;

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    setLoadingTools(true);
    try {
      const res = await axios.get(toolsUrl);
      if (res.data && res.data.tools) {
        setTools(res.data.tools);
      }
    } catch (err) {
      console.error("Failed to load MCP tools", err);
    } finally {
      setLoadingTools(false);
    }
  };

  const handleTestCall = async () => {
    if (!user || !user.email) return;
    setExecuting(true);
    setTestResult(null);

    try {
      let argsObj = {};
      try {
        argsObj = JSON.parse(testArgs || '{}');
      } catch (e) {
        setTestResult({ error: "Invalid JSON syntax in arguments." });
        setExecuting(false);
        return;
      }

      const rpcBody = {
        jsonrpc: "2.0",
        method: "tools/call",
        params: {
          name: selectedTool,
          arguments: {
            ...argsObj,
            login: user.email,
            pin: pin
          }
        },
        id: Date.now()
      };

      const res = await axios.post(`${API_BASE_URL || 'https://www.ailinkb.com'}/api/mcp`, rpcBody, {
        headers: {
          'Content-Type': 'application/json',
          'X-Login': user.email,
          'X-PIN': pin
        }
      });
      setTestResult(res.data);
    } catch (err) {
      if (err.response && err.response.data) {
        setTestResult(err.response.data);
      } else {
        setTestResult({ error: err.message || "Network error while calling MCP server" });
      }
    } finally {
      setExecuting(false);
    }
  };

  const claudeConfigExample = `{
  "mcpServers": {
    "ailinkb-crm": {
      "url": "${endpointUrl}",
      "headers": {
        "X-Login": "${user ? user.email : 'your-email@example.com'}",
        "X-PIN": "YOUR_6_DIGIT_PIN"
      }
    }
  }
}`;

  return (
    <Container size="xl" my="md">
      <Paper shadow="sm" radius="md" p="xl" withBorder mb="lg" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', color: 'white' }}>
        <Group justify="space-between" align="center" mb="sm">
          <Group>
            <Box p="sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12 }}>
              <IconServer size={32} color="#818cf8" />
            </Box>
            <Box>
              <Title order={2} c="white">{t('mcp_title', 'Konsola Administracyjna AI')}</Title>
            </Box>
          </Group>
        </Group>
      </Paper>

      <Grid mb="lg">
        <Grid.Col span={{ base: 12, md: 12 }}>
          <Card shadow="sm" p="lg" radius="md" withBorder h="100%">
            <Group mb="md">
              <IconKey size={24} color="#6366f1" />
              <Title order={4}>{t('mcp_auth_title', 'Uwierzytelnianie (Login i PIN)')}</Title>
            </Group>
            
            <Group grow mb="sm">
              <TextInput
                label={t('mcp_login_label', 'Twój Login (E-mail)')}
                value={user ? user.email : ''}
                readOnly
              />
              <PasswordInput
                label={t('mcp_pin_label', 'Twój PIN Bezpieczeństwa')}
                placeholder={t('mcp_pin_placeholder', 'Wpisz 6-cyfrowy PIN, aby testować...')}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      <Card shadow="sm" p="lg" radius="md" withBorder mb="lg">
        <Group mb="sm">
          <IconTerminal size={22} color="#4f46e5" />
          <Title order={4}>{t('mcp_tools_console', 'Konsola Narzędziowa')}</Title>
        </Group>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <Text fw={500} size="sm" mb="xs">{t('mcp_select_tool', 'Wybierz narzędzie do wywołania:')}</Text>
            <ScrollArea h={320} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
              {loadingTools ? (
                <Group justify="center" p="xl"><Loader size="sm" /></Group>
              ) : (
                tools.map((tItem) => (
                  <Box
                    key={tItem.name}
                    p="xs"
                    mb={6}
                    onClick={() => {
                      setSelectedTool(tItem.name);
                      if (tItem.name === 'website_get_preview') {
                        setTestArgs(JSON.stringify({ email: user ? user.email : '' }, null, 2));
                      } else if (tItem.name === 'crm_list_messages') {
                        setTestArgs(JSON.stringify({ limit: 5 }, null, 2));
                      } else if (tItem.name === 'website_setup') {
                        setTestArgs(JSON.stringify({ companyName: "Nowa Firma", description: "Opis", overwrite: false }, null, 2));
                      } else {
                        setTestArgs('{}');
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 6,
                      backgroundColor: selectedTool === tItem.name ? '#eef2ff' : 'transparent',
                      border: selectedTool === tItem.name ? '1px solid #6366f1' : '1px solid #f3f4f6'
                    }}
                  >
                    <Group justify="space-between">
                      <Text size="sm" fw={selectedTool === tItem.name ? 700 : 500} c={selectedTool === tItem.name ? 'indigo' : 'dark'}>
                        {tItem.name}
                      </Text>
                      {selectedTool === tItem.name && <Badge size="xs" color="indigo">{t('mcp_active_badge', 'Aktywne')}</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed" lineClamp={2}>{tItem.description}</Text>
                  </Box>
                ))
              )}
            </ScrollArea>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 7 }}>
            <Text fw={500} size="sm" mb="xs">{t('mcp_json_params', 'Parametry zapytania JSON (Arguments):')}</Text>
            <Code block style={{ height: 140, marginBottom: 12 }} contentEditable onChange={() => {}}>
              {testArgs}
            </Code>
            <TextInput
              value={testArgs}
              onChange={(e) => setTestArgs(e.target.value)}
              placeholder='{ "param": "value" }'
              mb="sm"
            />
            <Button
              color="indigo"
              leftSection={<IconTerminal size={16} />}
              onClick={handleTestCall}
              loading={executing}
              disabled={!pin}
              fullWidth
            >
              {pin ? t('mcp_test_btn', 'Przetestuj funkcję na żywo') : t('mcp_enter_pin_prompt', 'Wpisz PIN w panelu z lewej, aby wykonać test')}
            </Button>

            {testResult && (
              <Box mt="md" p="sm" style={{ backgroundColor: '#1e293b', borderRadius: 8, color: '#f8fafc', maxHeight: 220, overflowY: 'auto' }}>
                <Text size="xs" c="gray.4" mb={4}>{t('mcp_response_result', 'Wynik odpowiedzi Serwera MCP (JSON):')}</Text>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </Box>
            )}
          </Grid.Col>
        </Grid>
      </Card>
    </Container>
  );
}
